import { type GridRenderCellParams, GridValidRowModel } from '@mui/x-data-grid-pro';
import { cloneDeep } from 'lodash';
import React, { ReactNode, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CustomTooltipWithLabel,
  downloadFile,
  renderHeaderActionButtons,
  SidePopover,
  TableDashboard,
  useAppDispatch,
  useAppSelector,
} from '../../../globalUtils/globalExports';
import {
  getGlobalReducer,
  setTableColumnPrefrences,
} from '../../../rmsReduxStore/reduxExports';
import {
  getAcChargerReducer,
  fetchAcSpuriousChargersData,
  setAcSpuriousChargersListTableProps,
  exportToCsvForAcSpuriousChargersList,
} from '../../rmsReduxStore/acReduxExports';
import { acChargerTypes } from '../../rmsReduxStore/acReduxExports';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import './chargers.css';
import { Id, toast } from 'react-toastify';
import { ActionIcons } from '../../../globalUtils/globalIcons';
import { NoDataComponentSpuriousChargers } from '../../../globalUtils/TableDashboard/TableComponents';
import { formatRelativeTimeForTable } from '../../../globalUtils/globalHooks';
import access from '../../../auth/service/AccessControl';
import EditAcSpuriousCharger from './spuriousChargers/EditAcSpuriousCharger';

const AcSpuriousChargersDashboard = memo(function AcSpuriousChargersDashboard() {
  const dispatch = useAppDispatch();
  const layouts = ['list', 'map'];
  const { acSpuriousChargers, chargerParametersTableLoader, acSpuriousChargersListTableProps, acSpuriousChargersCount } = useAppSelector(getAcChargerReducer);
  const initialChargerData: AcSpuriousChargers = {
    id: '',
    chargerSerialNumber: '',
    macAddress: '',
    serverUrl: '',
    status: '',
    lastPingTime: '',
    createdAt: '',
    updatedAt: ''
  };
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [exportingFile, setExportingFile] = useState(false);
  const acSpuriousChargersListTablePropsRef = useRef(acSpuriousChargersListTableProps);
  const parametersRef = useRef(acSpuriousChargers);
  const { screenTableColumnPrefrences } = useAppSelector(getGlobalReducer);
  const [isSidePopOverOpen, setIsSidePopOverOpen] = useState(false);
  const [singleChargerData, setChargerSingleData] = useState(initialChargerData);

  useEffect(() => {
    acSpuriousChargersListTablePropsRef.current = acSpuriousChargersListTableProps;
  }, [acSpuriousChargersListTableProps]);

  useEffect(() => {
    parametersRef.current = acSpuriousChargers;
  }, [acSpuriousChargers]);

  const onChangeOfSearchText = useCallback(async (searchTerm) => {
    if (acSpuriousChargersListTablePropsRef?.current) {
      await layoutCallback(1, acSpuriousChargersListTablePropsRef?.current?.pageSize, 'list', acSpuriousChargersListTablePropsRef?.current?.sortBy, acSpuriousChargersListTablePropsRef?.current?.order, null, searchTerm);
    }
  }, [acSpuriousChargers, acSpuriousChargersListTablePropsRef?.current, parametersRef?.current]);

  const searchBoxConfiguration = useMemo(() => {
    return {
      searchFieldId: 'manage-ac-spurious-chargers-search-box',
      searchFieldName: 'manage-ac-spurious-chargers-search-box',
      handleSearch: onChangeOfSearchText,
    };
  }, [onChangeOfSearchText]);

  const getStatusIconClass = {
    'InProgress': 'status__in-progress__parameter__icon',
    'Scheduled': 'status__scheduled__parameter__icon',
    'Completed': 'status__completed__parameter__icon',
    'Enabled': 'status__completed__parameter__icon',
  };

  const headerActionBtns = renderHeaderActionButtons({
    actionBtns: [
      {
        id: 'export_csv_btn',
        isAccessible: access?.updateSetting?.export(),
        headerActionBtnClick: (event): void => {
          setExportAnchorEl(event?.currentTarget);
        }
      }
    ]
  });

  const onEditIconClick = (rowData) => {
    setIsSidePopOverOpen(true);
    setChargerSingleData(rowData);
  };

  const actionIconsList = useMemo(() => {
    return [{ icon: 'pencilIcon', isAccessible: access?.updateSetting?.edit(), action: (_, rowData) => onEditIconClick(rowData) }];
  }, [onEditIconClick]);

  const mobileViewConfiguration = useMemo(() => {
    return {
      headerDataConfig: {
        headerLeftDataConfig: ['chargerSerialNumber', 'macAddress', 'serverUrl'],
        headerRightDataConfig: {
          actionIconsComponent: true,
          statusIconComponent: true,
        },
      },
      statusIconKey: 'status',
      statusIconMapper: getStatusIconClass,
      contentDataConfig: [
        [
          { key: 'lastPingTime', iconClassName: '' },
          { key: 'createdAt', iconClassName: 'parameters_createdOn_icon' },
        ],
      ],
    };
  }, []);

  const getTableRows = useCallback((): AcSpuriousChargers[] => {
    return acSpuriousChargers !== null && acSpuriousChargers?.length > 0 ? acSpuriousChargers : [];
  }, [acSpuriousChargers]);

  const acSpuriousChargerColumns = useMemo(() => {
    return [
      {
        field: 'chargerSerialNumber',
        headerName: 'Charger Serial Number',
        maxWidth: 500,
        sortable: true,
        minWidth: 200,
        hideable: false,
      },
      {
        field: 'macAddress',
        headerName: 'Mac Address',
        flex: .6,
        sortable: true,
        minWidth: 80,
      },
      {
        field: 'chargerOCPPID',
        headerName: 'Charger OCPP ID',
        flex: .6,
        sortable: true,
        minWidth: 170,
        renderCell: (params: GridRenderCellParams<GridValidRowModel>): ReactNode => {
          const rowData = params?.row;
          return <CustomTooltipWithLabel label={rowData.chargerOCPPID} labelId={rowData.chargerOCPPID} />;
        },
      },
      {
        field: 'serverUrl',
        headerName: 'Server URL',
        flex: .6,
        sortable: true,
        minWidth: 170,
        renderCell: (params: GridRenderCellParams<GridValidRowModel>): ReactNode => {
          const rowData = params?.row;
          return <CustomTooltipWithLabel label={rowData.serverUrl} labelId={rowData.serverUrl} />;
        },
      },
      {
        field: 'lastPingTime',
        flex: .6,
        headerName: 'Last Ping Time',
        minWidth: 150,
        renderCell: (params: GridRenderCellParams<GridValidRowModel>): ReactNode => {
          const rowData = params?.row;
          const date = new Date(rowData?.lastPingTime);
          return formatRelativeTimeForTable(date);
        },
      },
      {
        field: 'chargerStatus',
        headerName: 'Status',
        flex: .6,
        sortable: true,
        minWidth: 120,
      },
      {
        field: 'chargingStatus',
        headerName: 'Charging Status',
        flex: .6,
        sortable: true,
        minWidth: 120,
      },
      {
        field: 'createdAt',
        flex: .6,
        headerName: 'Created At',
        minWidth: 150,
        renderCell: (params: GridRenderCellParams<GridValidRowModel>): ReactNode => {
          const rowData = params?.row;
          const date = new Date(rowData?.createdAt);
          return formatRelativeTimeForTable(date);
        },
      },
      {
        field: 'View',
        headerName: 'Actions',
        flex: 1,
        sortable: false,
        minWidth: 90,
        maxWidth: 100,
        headerAlign: 'center',
        renderCell: (params: GridRenderCellParams<GridValidRowModel, Date>): JSX.Element => {
          const rowData = params?.row;
          if (rowData?.chargerStatus !== 'Offline' && rowData?.chargingStatus === 'Idle') {
            return (
              <div className="charging__station__action__icons__wrap">
                <ActionIcons actionIconsList={actionIconsList} data={rowData} />
              </div>
            );
          }
          return <div></div>;
        },
      },
    ];
  }, []);

  const layoutCallback = useCallback(async (pageNumber: number, pageSize: number, view: string, sortField: string, sortOrder: string, filterData, searchTerm) => {
    const tableProps: IChargerListTableProps = cloneDeep(acSpuriousChargersListTablePropsRef?.current);

    await dispatch(fetchAcSpuriousChargersData({
      sortBy: sortField || acSpuriousChargersListTablePropsRef?.current?.sortBy,
      order: sortOrder || acSpuriousChargersListTablePropsRef?.current?.order,
      pageSize: pageSize || 15,
      pageNumber,
      filters: null,
      searchTerm: searchTerm != null && searchTerm != undefined ? searchTerm : acSpuriousChargersListTablePropsRef?.current?.searchTerm
    }));

    if (tableProps) {
      tableProps.view = view ?? 'list';
      tableProps.sortBy = sortField || acSpuriousChargersListTablePropsRef?.current?.sortBy;
      tableProps.order = sortOrder || acSpuriousChargersListTablePropsRef?.current?.order;
      tableProps.pageNumber = pageNumber;
      tableProps.pageSize = pageSize || 15;
      if (searchTerm != null && searchTerm != undefined) {
        tableProps.searchTerm = searchTerm;
      }
    }
    await dispatch(setAcSpuriousChargersListTableProps(tableProps));
  }, [acSpuriousChargersListTablePropsRef?.current, dispatch]);

  const handleColumnPrefrenceChange = useCallback(async (operation, data) => {
    await dispatch(setTableColumnPrefrences('acSpuriousChargers', operation, data));
  }, []);

  const acSpuriousChargersListCleanUpState = useCallback(() => {
    const action = {
      type: acChargerTypes.CLEAR_AC_SPURIOUS_CHARGERS_LIST_DATA,
    };
    dispatch(action);
  }, []);

  useEffect(() => {
    window.addEventListener('beforeunload', acSpuriousChargersListCleanUpState);
    return (): void => {
      acSpuriousChargersListCleanUpState();
      window.removeEventListener('beforeunload', acSpuriousChargersListCleanUpState);
    };
  }, []);

  const handleClose = (): void => {
    setExportAnchorEl(null);
  };

  const fileDownloadSuccessToast = useCallback((): Id => {
    setExportingFile(false);
    return toast.success('File downloaded.');
  }, []);

  const fileDownloadFailureToast = useCallback((): Id => {
    setExportingFile(false);
    return toast.warn('Something went wrong. Please export again.');
  }, []);

  const onExportBtnClick = useCallback(async (fileType: 'csv' | 'xlsx'): Promise<void> => {
    if (exportingFile) return;
    try {
      const response = await dispatch(exportToCsvForAcSpuriousChargersList({
        fileType: fileType,
        filters: []
      }));
      if (response?.status === 202 || response?.status === 200) {
        downloadFile({ url: response?.data?.url });
        fileDownloadSuccessToast();
      } else {
        fileDownloadFailureToast();
        setExportAnchorEl(null);
      }
    } finally {
      setExportingFile(false);
      setExportAnchorEl(null);
    }
  }, [exportingFile, fileDownloadSuccessToast, fileDownloadFailureToast]);

  return (
    <>
      <TableDashboard
        tableId="manage__ac__spurious_chargers__table"
        tableClassName="manage__spurious_chargers__table"
        header={'Spurious Chargers'}
        searchBoxIncluded={true}
        searchBoxConfiguration={searchBoxConfiguration}
        gridColumns={acSpuriousChargerColumns}
        tableRows={getTableRows()}
        mobileViewConfiguration={mobileViewConfiguration}
        layouts={layouts}
        layoutView={'list'}
        headerActionBtns={headerActionBtns}
        listLayoutCallBack={layoutCallback}
        totalCount={acSpuriousChargersCount ?? 0}
        showSkeleton={!acSpuriousChargers}
        totalCountText={'spurious chargers'}
        showLoader={chargerParametersTableLoader}
        customNoDataComponent={NoDataComponentSpuriousChargers}
        pageNumber={acSpuriousChargersListTableProps?.pageNumber}
        tablePageSize={acSpuriousChargersListTableProps?.pageSize}
        sortField={acSpuriousChargersListTableProps?.sortBy}
        sortOrder={acSpuriousChargersListTableProps?.order}
        referenceDataCallbacksFilters={['']}
        columnPrefrences={screenTableColumnPrefrences?.find((item) => item.screen === 'acSpuriousChargers')}
        handleColumnPrefrenceChange={handleColumnPrefrenceChange}
      />

      <Menu
        id="export-option-dropdown"
        anchorEl={exportAnchorEl}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        keepMounted
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        className='export__option__dropdown'
        sx={{ top: '30px' }}
        open={Boolean(exportAnchorEl)}
        onClose={handleClose}
      >
        <MenuItem>
          <div className='export__btn-option' onClick={(e) => { e?.stopPropagation(); onExportBtnClick('csv'); }}>
            To CSV
          </div>
        </MenuItem>
        <MenuItem>
          <div className='export__btn-option' onClick={(e) => { e?.stopPropagation(); onExportBtnClick('xlsx'); }}>
            To Excel
          </div>
        </MenuItem>
      </Menu>

      <SidePopover
        id={'ac_spurious_charger_update'}
        headerText={'Update Charger'}
        open={isSidePopOverOpen}
        handleClose={() => { setIsSidePopOverOpen(false); }}
        // eslint-disable-next-line react/no-children-prop
        children={
          <EditAcSpuriousCharger
            chargerData={singleChargerData}
            handleCloseSidePopover={() => { setIsSidePopOverOpen(false); }}
          />
        }
      />
    </>
  );
});

export default AcSpuriousChargersDashboard;
